import React from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useVideo } from '@platform/core/src/context/VideoContext';
import { alpha } from '@mui/material/styles';

// YouTube SVG Icon component
const YouTubeIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#FF0000">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const VideoLinkTag = ({ children }) => {
  const { playVideo } = useVideo();
  
  const content = React.Children.toArray(children)
    .map(child => (typeof child === 'string' ? child : ''))
    .join('')
    .trim();

  if (!content) return null;

  // Format: [V]Title:::URL:::Thumbnail[/V]
  const parts = content.split(':::');
  
  const title = parts[0];
  const url = parts[1];
  const thumbnail = parts[2]; 

  if (!title || !url) {
    return <span>{children}</span>;
  }

  // Check if URL is from YouTube
  const isYouTube = url && (
    url.includes('youtube.com') || 
    url.includes('youtu.be') || 
    url.includes('youtube-nocookie.com')
  );

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    playVideo(url, title);
  };

  return (
    <Box
      component="span"
      onClick={handleClick}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        cursor: 'pointer',
        backgroundColor: alpha(theme.palette.background.paper, 0.85),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        padding: '4px 10px 4px 4px',
        margin: '2px 4px',
        maxWidth: '100%',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.06)}`,
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          borderColor: isYouTube 
            ? alpha('#FF0000', 0.4) 
            : theme.palette.primary.main,
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(isYouTube ? '#FF0000' : theme.palette.primary.main, 0.15)}`,
          '& .video-thumbnail-overlay': {
            backgroundColor: alpha(theme.palette.common.black, 0.3),
          }
        }
      })}
    >
      {/* Thumbnail Container */}
      <Box 
        sx={{ 
          width: 48, 
          height: 36, 
          mr: 1.25,
          borderRadius: '4px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {thumbnail ? (
          <Box 
            component="img" 
            src={thumbnail} 
            alt=""
            sx={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
            }} 
          />
        ) : (
          <Box 
            sx={{ 
              width: '100%', 
              height: '100%', 
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            }} 
          />
        )}
        
        {/* Play Overlay */}
        <Box 
          className="video-thumbnail-overlay"
          sx={{ 
            position: 'absolute', 
            inset: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: alpha('#000', 0.2),
            transition: 'background-color 0.2s ease',
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: alpha('#fff', 0.95),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 14, color: '#000', ml: '2px' }} />
          </Box>
        </Box>
      </Box>
      
      {/* Text Content */}
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
        <Typography 
          variant="body2" 
          component="span" 
          sx={{ 
            fontWeight: 600, 
            fontSize: '0.8rem',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
          }}
        >
          {title}
        </Typography>
        
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          {isYouTube ? (
            <>
              <YouTubeIcon size={14} />
              <Typography 
                variant="caption" 
                component="span" 
                sx={{ 
                  fontSize: '0.7rem', 
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                YouTube
              </Typography>
            </>
          ) : (
            <Typography 
              variant="caption" 
              component="span" 
              sx={{ 
                fontSize: '0.7rem', 
                color: 'primary.main',
                fontWeight: 500,
              }}
            >
              Watch Video
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VideoLinkTag;